/*
  Domain vocabulary and the shapes that cross a boundary.

  These live in contracts rather than in core because every one of them appears in a command payload,
  in the project-state response, or both — so the zod schema that validates the request is the same
  one TypeScript infers the type from, and (from P6) the same one the LLM tool definition is generated
  from. core imports these and adds the behaviour.
*/
import { z } from 'zod';
/* ------------------------------------------------------------------ *
 * Vocabulary
 * ------------------------------------------------------------------ */
/** Separation Studio's three destinations for a person. Mirrors BUCKETS in orgchart.html. */
export const BUCKETS = ['Remain', 'Carve-out', 'No longer required'];
export const bucketSchema = z.enum(BUCKETS);
/** Everyone starts in Remain; the carve-out is what you actively designate. */
export const DEFAULT_BUCKET = 'Remain';
/** How a decision came to be: a user's explicit choice, or materialised by the perimeter rule. */
export const decisionSourceSchema = z.enum(['manual', 'rule']);
export const perspectiveSchema = z.enum(['as-is', 'remain', 'carve-out']);
/**
 * The three perspectives plus the target blueprint.
 *
 * 'target' is not a perspective you can look at the as-is org through — it is the designed end state —
 * but it carries head designations and placement overrides with exactly the same shape, so it shares
 * the mechanism rather than duplicating three tables.
 */
export const headPerspectiveSchema = z.enum(['as-is', 'remain', 'carve-out', 'target']);
/** The carve-out design stages. The stage lens refines the carve-out perspective only. */
export const STAGES = ['perimeter', 'day1', 'day2'];
export const stageSchema = z.enum(STAGES);
/** When a person joins the carve-out: with the dedicated perimeter, or at Day 1. */
export const joinStageSchema = z.enum(['perimeter', 'day1']);
export const strandedActionSchema = z.enum(['rightsize', 'redeploy']);
export const waveSchema = z.enum(['day1', 'day2']);
export const tsaDirectionSchema = z.enum(['received', 'provided']);
export const tsaStatusSchema = z.enum(['planned', 'agreed', 'exited']);
/**
 * How a legal entity is treated in the deal. Ported verbatim from ENTITY_CLASSES
 * (orgchart.html:7699) — these exact four values drive the entity-flow analytics, and seeding a
 * registry from the roster classifies everything `retain` until somebody says otherwise.
 */
export const entityClassificationSchema = z.enum(['retain', 'transfer', 'new', 'dissolve']);
export const DEFAULT_ENTITY_CLASSIFICATION = 'retain';
export const viewLayoutSchema = z.enum(['grouping', 'reporting']);
export const viewModeSchema = z.enum(['hide', 'grey']);
export const projectRoleSchema = z.enum(['owner', 'editor', 'reviewer', 'viewer']);
/** Roles that may issue mutating commands. A reviewer proposes instead; see proposals. */
const WRITER_ROLES = new Set(['owner', 'editor']);
export function canWrite(role) {
    return WRITER_ROLES.has(role);
}
export function canAdminProject(role) {
    return role === 'owner';
}
/** A reviewer can raise a proposal; a viewer cannot. Writers apply directly and need no proposal. */
export function canPropose(role) {
    return role === 'owner' || role === 'editor' || role === 'reviewer';
}
/**
 * Who may put something on the record without changing anything.
 *
 * A reviewer's whole purpose is seeing a problem and saying so, and `canWrite` — which gates every
 * command — locked them out of `addNote` and `annotateEvent` along with everything else. The declared
 * alternative, raising a proposal, has no client surface and is P6 work. So the role opened a project,
 * read the design, opened History, and had no way to leave a remark: their only trace in the whole
 * product was an avatar in the presence row.
 *
 * A note is not a write in the sense `canWrite` means. Both handlers return NO writes and an EMPTY
 * inverse (`packages/core/src/commands/settings.ts`) — the event IS the note, and the log is
 * append-only, so a remark cannot alter a decision and cannot be reverted into one either.
 *
 * A VIEWER IS DELIBERATELY OUTSIDE THIS. "Read and export, changes nothing" is the role, and an entry in
 * the audit trail is a change to it. The membership therefore coincides with `canPropose` today, which is
 * not a coincidence worth collapsing: they answer different questions — "may speak" and "may propose" —
 * and a project that later let observers comment without proposing would need them apart.
 */
export function canComment(role) {
    return role === 'owner' || role === 'editor' || role === 'reviewer';
}
/* ------------------------------------------------------------------ *
 * Identifiers
 *
 * Employee ids and group values become object keys and database keys, so they are bounded and
 * screened here rather than at each use. `__proto__` as an employee id is not a hypothetical: the id
 * comes from a client's spreadsheet.
 * ------------------------------------------------------------------ */
const FORBIDDEN_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
export const idSchema = z
    .string()
    .min(1)
    .max(200)
    .refine((v) => !FORBIDDEN_KEYS.has(v), { message: 'Reserved identifier' });
/** A displayable label from client data: bounded, but otherwise theirs to choose. */
export const labelSchema = z.string().max(500);
/** A logical field key: a mapped column, a hierarchy level, or a target level. */
export const fieldKeySchema = z
    .string()
    .min(1)
    .max(100)
    .refine((v) => !FORBIDDEN_KEYS.has(v), { message: 'Reserved identifier' });
/**
 * A month relative to Day 1, on the stranded plan's three-year window.
 *
 * Kept at 36 because the plan editor is where the frozen app also clamps to 36, so a stored plan month
 * outside it has never been representable. Anything on the TSA CLOCK uses `agreementMonthSchema` instead
 * — see the note there for why the two cannot be one bound.
 */
export const monthSchema = z.number().int().min(0).max(36);
/**
 * When a stranded plan acts, if the caller does not say and the person has no plan yet.
 *
 * Six months, which is the frozen app's own assumption about how long it takes to act on a stranded
 * role — `prev ? prev.month : 6` (orgchart.html:6441). It lives HERE rather than in the RemainCo panel,
 * where it used to be the only copy: `setStrandedPlan` defaulted the field to 0, meaning "effective
 * immediately", so the panel and the schema stated two different assumptions and only the panel's ever
 * reached a user. Anything else driving the command — a proposal, a script, the assistant — got the
 * other one.
 */
export const DEFAULT_STRANDED_MONTH = 6;
/**
 * A month on the TSA clock, which runs as long as the longest agreement.
 *
 * `durationMonths` has always been 0..120, and a replacement's `neededBy` was 0..36. That was invisible
 * while every chart clamped its axis to 36 — and became a hole the moment the axis followed the data: for
 * any agreement longer than 36 months a replacement could not be dated after it ends, so `late` could
 * never fire and `covered` was true by construction. The pane's central question — is the replacement
 * ready before the clock runs out — answered itself for exactly the long agreements where it matters most.
 *
 * The frozen app has no upper bound at all here (`Math.max(0, Math.round(Number(nb)))`, orgchart.html:7232,
 * with a bare `min="0"` input at :5052), so widening to the duration ceiling loses nothing and refuses
 * less than the app does.
 */
export const agreementMonthSchema = z.number().int().min(0).max(120);
/* ------------------------------------------------------------------ *
 * Domain shapes
 * ------------------------------------------------------------------ */
export const groupingLevelSchema = z.object({ key: fieldKeySchema, label: labelSchema });
/**
 * How deep the target ladder may go.
 *
 * One number rather than four literals, because they have to agree: a unit path, a `setTargetLevels`
 * ladder, a `reparentTargetUnit` destination and a `renameTargetValue` level index all address the
 * same tiers. `reparentTargetUnit` grows the ladder to fit a moved subtree and used to grow it past
 * this, producing units nothing could then remove, rename or place into.
 */
export const MAX_TARGET_LEVELS = 12;
/**
 * A declared target unit, as the path of level values that identifies it.
 *
 * A path rather than an id because the target org is designed top-down and a unit's identity IS its
 * position — the same content-addressing the as-is hierarchy uses, and the same rename hazard.
 */
export const targetUnitSchema = z.array(labelSchema).min(1).max(MAX_TARGET_LEVELS);
/**
 * Months of severance — one bound wherever that number appears, the global default and a grid tier
 * alike. They measure the same thing, and a range a UI cannot re-display is the same defect twice.
 *
 * A grid VALUE stays a union with string, because an older project file may carry `'12'` and the
 * importer preserves what it was given; every writer produces a number.
 */
export const severanceMonthsSchema = z.number().min(0).max(120);
/**
 * One FX rate, multiplied onto a salary to land it in the reporting currency.
 *
 * Strictly positive with no upper cap that fits every direction: reporting in EUR against IDR
 * payrolls needs 0.00005, reporting in IDR needs 17,000. Zero is refused at the schema because a
 * zero rate zeroes every salary it touches — the same reasoning as `loadedCostMultiplier`'s handler
 * guard, stated here so an imported value meets it too.
 */
export const fxRateSchema = z.number().positive().max(1_000_000_000);
export const assumptionsSchema = z.object({
    /** Salary → fully-loaded cost. Applied ONLY by the economics surfaces, never to org views. */
    loadedCostMultiplier: z.number().min(0).max(100),
    severanceMonthsDefault: severanceMonthsSchema,
    /** Open-ended maps keyed by the client's own country and grade values. */
    severanceByCountry: z.record(z.string(), z.union([z.number(), z.string()])),
    severanceBySeniority: z.record(z.string(), z.union([z.number(), z.string()])),
    /** One-off hiring cost as a percentage of annual base salary. */
    hiringCostPct: z.number().min(0).max(1000),
    /**
     * One-off retention as a percentage of annual base salary, for the people who must STAY to their
     * own exit: carve-out Day 2 exits and RemainCo stranded rightsizings. The model already knows who
     * carries stranded cost until month M and prices their severance; this prices the bonus that gets
     * them to M — severance without retention reads as naïve to any CFO. Zero (the default) prices
     * nothing, so every existing case is unchanged until somebody states a budget.
     */
    retentionPct: z.number().min(0).max(1000),
    /**
     * Per-country loaded-cost multiplier, overriding the global one. The single global multiplier is
     * the crudest assumption in the product: Belgian employer charges and US employer charges are not
     * one number. Same open-map shape and inherit rule as every grid — no tier, the global applies.
     */
    loadedCostMultiplierByCountry: z.record(z.string(), z.union([z.number(), z.string()])),
    /**
     * Salary conversion INTO the reporting currency, keyed by the roster's own currency codes.
     *
     * The same open-map shape as the severance grids, with the same inherit rule pointed the other
     * way: an absent or blank tier means × 1 — the person's salary is already in the reporting
     * currency — so a project that never maps a currency column computes bit-identical numbers.
     * Values union with string for the same reason the severance grids do: an older file's encoding
     * is preserved on import; every writer produces numbers.
     */
    fxRates: z.record(z.string(), z.union([z.number(), z.string()])),
    /**
     * What the converted numbers are IN — a label, not an arithmetic input.
     *
     * Nullable because most projects are single-currency and never say; the rates alone determine
     * every figure. Naming it is for captions, diagnostics and export notes, where "converted at
     * project rates" needs a currency to name.
     */
    reportingCurrency: z.string().max(12).nullable(),
    /**
     * When Day 1 IS — the date the whole month vocabulary counts from.
     *
     * Every clock in the model runs in integer months after Day 1, and until this existed Day 1
     * itself had no date: a stranded plan could say M6 and never September. A label like the
     * reporting currency — the arithmetic stays on the month indices, which is what keeps every
     * engine untouched — but the reading surfaces (the monthly curves, the exports) can finally
     * print real months. ISO YYYY-MM-DD, because a date label that needs a locale to parse is not
     * a label; formatting happens where it is read.
     */
    day1Date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    /**
     * Consultation lead time per country, in months before a departure can lawfully take effect.
     *
     * The timeline of a European carve-out is made by consultation, not design — and the model let a
     * stranded plan rightsize a French carrier at M2 with nothing to say a PSE alone eats most of
     * that. A WARNING input, deliberately not an enforcement: diagnostics flag plan months inside
     * the lead time, and the person planning stays free to record the plan the lawyers are still
     * negotiating. Same open-map shape as the severance grids, same inherit rule: no tier, no claim.
     */
    consultationMonthsByCountry: z.record(z.string(), z.union([z.number(), z.string()])),
    /**
     * The month a country's transfer actually closes, counted from Day 1. Absent = closes at Day 1.
     *
     * Complex deals close in waves: regulatory approvals, works-council completion and licence
     * transfers push whole countries months past the principal close, and until this existed the
     * model silently assumed simultaneity. Whole months, 1..120, on the same clock as every other
     * month in the model. It shifts when stranded cost STARTS (a business still owned strands
     * nothing) and feeds the timing warnings; the stage bridge deliberately ignores it — deferral
     * is when the perimeter transfers, never whether.
     */
    closeMonthByCountry: z.record(z.string(), z.union([z.number(), z.string()])),
});
/** The keys `setAssumption` accepts. The three grids take a sub-key as well. */
export const ASSUMPTION_KEYS = [
    'loadedCostMultiplier',
    'severanceMonthsDefault',
    'severanceByCountry',
    'severanceBySeniority',
    'hiringCostPct',
    'fxRates',
    'reportingCurrency',
    'day1Date',
    'consultationMonthsByCountry',
    'retentionPct',
    'loadedCostMultiplierByCountry',
    'closeMonthByCountry',
];
export const assumptionKeySchema = z.enum(ASSUMPTION_KEYS);
export const bucketMetaSchema = z.object({
    src: decisionSourceSchema,
    joinStage: joinStageSchema,
    /** This position is cut again at Day 2 rightsizing. */
    day2Exit: z.boolean().optional(),
});
export const strandedPlanEntrySchema = z.object({
    action: strandedActionSchema,
    /** Effective FROM this month after Day 1. */
    month: monthSchema,
});
export const tsaSchema = z.object({
    id: idSchema,
    name: labelSchema,
    service: labelSchema.nullable(),
    area: labelSchema.nullable(),
    /** received = the carve-out pays RemainCo; provided = RemainCo pays the carve-out (reverse). */
    direction: tsaDirectionSchema,
    durationMonths: agreementMonthSchema,
    monthlyCost: z.number().min(0),
    status: tsaStatusSchema,
    /**
     * The month an EXITED agreement actually stopped, on the same clock as `durationMonths`.
     *
     * `status` alone is all-or-nothing: an agreement exited at month 8 of a twelve-month term billed for
     * eight months, and the model could not say so. It billed the full term until P4j-b and nothing at all
     * after it — the second error is the smaller and errs toward MORE exposure, but it is still wrong for
     * every agreement ended mid-term.
     *
     * Null means "no month recorded", which keeps the previous behaviour exactly: an exited agreement with
     * no month bills nothing, which is what the parity harness compares against, because the frozen app
     * has no such field at all. Read only when `status === 'exited'`; on any other status it is stale data
     * from a previous decision and is deliberately ignored rather than cleared, so flipping a status back
     * and forth does not lose the date.
     */
    exitedAtMonth: agreementMonthSchema.nullable().default(null),
    note: z.string().max(4000).nullable(),
});
/** The editable fields of a TSA; the id is not among them. */
export const tsaPatchSchema = tsaSchema.omit({ id: true }).partial();
/** A planned hire. Structurally a person, plus arrival timing and TSA linkage. */
export const buildUpSchema = z.object({
    id: idSchema,
    name: labelSchema,
    /**
     * The employee this hire reports to, or null for top level.
     *
     * Only the reporting-line layout reads it — the grouping layout places a build-up by its level values
     * like anybody else — which is why the app's form calls it "optional for grouped views". Optional is
     * not the same as ignorable: dropping it on import severs the line silently, and nothing on screen
     * says a manager used to be there.
     */
    supervisorId: idSchema.nullable(),
    jobTitle: labelSchema.nullable(),
    location: labelSchema.nullable(),
    country: labelSchema.nullable(),
    /**
     * Defaulted null, which for a planned hire means: the salary is entered in the reporting
     * currency and prices at × 1. The default also keeps every stored pre-currency command valid.
     */
    currency: labelSchema.nullable().default(null),
    /** A planned position has no pension history; defaulted null keeps stored commands valid. */
    pensionScheme: labelSchema.nullable().default(null),
    seniority: labelSchema.nullable(),
    businessUnit: labelSchema.nullable(),
    legalEntity: labelSchema.nullable(),
    salary: z.number().nullable(),
    fte: z.number().nullable(),
    levels: z.record(fieldKeySchema, labelSchema.nullable()),
    wave: waveSchema,
    /** The TSA this hire replaces, if any. */
    tsaId: idSchema.nullable(),
    /** Month the replacement must be in place by. */
    /** On the TSA clock, not the plan's — an agreement can run to M120, so its replacement must be datable there. */
    neededBy: agreementMonthSchema.nullable(),
    day2Exit: z.boolean(),
    /**
     * Reserved, and deliberately not on any form.
     *
     * It round-trips through the column, the loaders and the exporter, but nothing reads it — and the
     * frozen app has no such field at all. A Day 2 hire's ramp-in is driven by `neededBy` today. Left
     * here so an imported platform file keeps it, and documented so nobody adds a control for a number
     * that moves nothing.
     */
    /** When the hire arrives. On the same clock as neededBy, for the same reason. */
    startMonth: agreementMonthSchema.nullable(),
});
export const buildUpPatchSchema = buildUpSchema.omit({ id: true }).partial();
export const legalEntitySchema = z.object({
    id: idSchema,
    name: labelSchema,
    country: labelSchema.nullable(),
    classification: entityClassificationSchema,
});
/* ---------- filters and views ---------- */
/**
 * One filter condition, in the shape the ported engine already evaluates.
 *
 * Not an operator model: matching is AND across fields, OR within one field's selected `values`, with
 * a min/max `range` for numeric fields. That is `passesFilters` in core, held to the shipped app's
 * output by the parity harness — so this schema follows it rather than proposing a second semantics.
 * A blank numeric value never satisfies a range, because unknown is not zero.
 */
export const filterConditionSchema = z.object({
    fieldId: fieldKeySchema,
    label: labelSchema,
    numeric: z.boolean().optional(),
    values: z.array(z.string().max(500)).max(5000).optional(),
    range: z
        .object({
        min: z.union([z.number(), z.string(), z.null()]).optional(),
        max: z.union([z.number(), z.string(), z.null()]).optional(),
    })
        .nullable()
        .optional(),
    /** A condition may target its own perspective, independent of the active one. */
    perspective: z.string().max(50).optional(),
});
/** A custom pivot: group by these fields instead of the canonical hierarchy, for viewing only. */
export const viewGroupingSchema = z
    .array(z.object({ fieldId: fieldKeySchema, label: labelSchema }))
    .max(6);
export const savedViewSchema = z.object({
    id: idSchema,
    name: labelSchema,
    viewGrouping: viewGroupingSchema.nullable(),
    filters: z.array(filterConditionSchema).max(20),
    viewLayout: viewLayoutSchema,
    isDefault: z.boolean(),
});
export const savedViewPatchSchema = savedViewSchema.omit({ id: true, isDefault: true }).partial();
/* ---------- display configuration ---------- */
/**
 * Which figures each kind of node card shows.
 *
 * Shared project state rather than a personal preference, deliberately: the deck exporter reads the
 * same config, so the promise that a slide shows what the screen shows only holds if there is one
 * answer per project. Ported from NODE_DISPLAY_DEFAULT (orgchart.html:9596).
 */
const hexColour = z.string().regex(/^[0-9A-Fa-f]{6}$/, 'Six hex digits, no leading #');
export const nodeDisplaySchema = z.object({
    person: z.object({
        jobTitle: z.boolean(),
        location: z.boolean(),
        salary: z.boolean(),
        fte: z.boolean(),
        dedication: z.boolean(),
        stageMarkers: z.boolean(),
        status: z.boolean(),
        rollup: z.boolean(),
    }),
    group: z.object({
        headOf: z.boolean(),
        levelTag: z.boolean(),
        headcount: z.boolean(),
        salary: z.boolean(),
        fte: z.boolean(),
        stageCounts: z.boolean(),
        compositionBar: z.boolean(),
    }),
    root: z.object({
        headOf: z.boolean(),
        headcount: z.boolean(),
        salary: z.boolean(),
        fte: z.boolean(),
        stageCounts: z.boolean(),
        compositionBar: z.boolean(),
    }),
    /** Which metrics and which stages the group stage-comparison line renders. */
    stageLine: z.object({
        hc: z.boolean(),
        fte: z.boolean(),
        cost: z.boolean(),
        asis: z.boolean(),
        p: z.boolean(),
        d1: z.boolean(),
        d2: z.boolean(),
        r1: z.boolean(),
        rs: z.boolean(),
    }),
    colors: z.object({
        box: z.object({ root: hexColour, person: hexColour, group: hexColour }),
        buildUp: hexColour,
        status: z.object({
            Remain: hexColour,
            'Carve-out': hexColour,
            'No longer required': hexColour,
        }),
    }),
    /** Deck box geometry, in inches. */
    box: z.object({
        bw: z.number().min(0.1).max(20),
        bh: z.number().min(0.1).max(20),
        hgap: z.number().min(0).max(20),
        vgap: z.number().min(0).max(20),
    }),
});
export function freshNodeDisplay() {
    return {
        person: { jobTitle: true, location: true, salary: true, fte: true, dedication: true, stageMarkers: true, status: true, rollup: true },
        group: { headOf: true, levelTag: true, headcount: true, salary: true, fte: true, stageCounts: false, compositionBar: true },
        root: { headOf: true, headcount: true, salary: true, fte: true, stageCounts: false, compositionBar: true },
        stageLine: { hc: true, fte: false, cost: false, asis: false, p: true, d1: true, d2: true, r1: false, rs: false },
        colors: {
            box: { root: '0C233C', person: 'FFFFFF', group: '00338D' },
            buildUp: 'fd349c',
            status: { Remain: '00338d', 'Carve-out': '7213ea', 'No longer required': 'ef4444' },
        },
        box: { bw: 2.0, bh: 0.82, hgap: 0.3, vgap: 0.55 },
    };
}
/** Slide title and content boxes, in centimetres. Ported from PPT_GEOM_DEFAULT (:9582). */
const pptBoxSchema = z.object({
    x: z.number().min(0).max(200),
    y: z.number().min(0).max(200),
    w: z.number().min(0.1).max(200),
    h: z.number().min(0.1).max(200),
});
export const pptxGeomSchema = z.object({ title: pptBoxSchema, content: pptBoxSchema });
export function freshPptxGeom() {
    return {
        title: { x: 2.77, y: 1.2, w: 28.35, h: 1.48 },
        content: { x: 2.77, y: 3.7, w: 28.35, h: 12.63 },
    };
}
//# sourceMappingURL=domain.js.map